import moment from 'moment';
import 'moment-timezone';

/**
 * Преобразует объект Date или строку в строку формата 'DD.MM.YYYY' по часовому поясу 'Europe/Moscow'.
 *
 * @param {Date | string} date - Входная дата.
 * @returns {string | null} - Отформатированная строка или null, если дата некорректна.
 */
export function convertDateToString(date) {
  // Проверяем, что дата существует и может быть распознана как валидная дата moment.js
  if (date && moment(date).isValid()) {
    // Преобразуем в момент-объект с часовым поясом Europe/Moscow и форматируем без времени
    return moment(date).tz('Europe/Moscow').format('DD.MM.YYYY');
  } else {
    // Если дата не задана или некорректна — возвращаем null
    return null;
  }
}

/**
 * Преобразует объект Date или строку в строку формата 'DD.MM.YYYY HH:mm' по часовому поясу 'Europe/Moscow'.
 *
 * @param {Date | string} date - Входная дата.
 * @returns {string | null} - Отформатированная строка с датой и временем или null, если дата некорректна.
 */
export function convertDateToStringWithTime(date) {
  // Проверяем, что дата задана и корректна
  if (date && moment(date).isValid()) {
    // Преобразуем в moment и форматируем с учетом часового пояса и времени
    return moment(date).tz('Europe/Moscow').format('DD.MM.YYYY HH:mm');
  } else {
    // При некорректной дате возвращаем null
    return null;
  }
}

/**
 * Конвертирует дату (строку или объект Date) в объект Date по часовому поясу 'Europe/Moscow'.
 * Работает как с ISO-строками, так и с форматом 'DD.MM.YYYY'.
 *
 * @param {string | Date} dateString - Входная дата (строка или объект Date).
 * @returns {Date | null} - Возвращает объект Date, приведённый к московскому времени, либо null при ошибке.
 */
export function convertToDate(dateString) {
  // Проверка: если дата не передана — возвращаем null
  if (typeof dateString !== 'string' || !dateString) return null;

  // Если это уже объект Date — интерпретируем его как UTC и переводим в Europe/Moscow
  if (dateString instanceof Date) {
    return moment(dateString).tz('Europe/Moscow').toDate();
  }

  // Если строка содержит символ 'T', предполагаем ISO-формат (например: '2025-06-30T21:00:00.000Z')
  // Иначе — парсим как строку формата 'DD.MM.YYYY'
  const date = dateString.includes('T')
    ? moment.tz(dateString, 'Europe/Moscow') // автоматически парсится как ISO и интерпретируется в указанном таймзоне
    : moment.tz(dateString, 'DD.MM.YYYY', 'Europe/Moscow'); // формат вручную указывается

  // Возвращаем сконвертированный объект Date, уже в часовом поясе Europe/Moscow
  return date.toDate();
}

/**
 * Преобразует миллисекунды в форматированное время в формате "часы:минуты".
 *
 * @param {number} milliseconds - Количество миллисекунд для преобразования.
 * @returns {string|null} Возвращает строку времени в формате "HH:mm".
 *                        В случае ошибки возвращает null.
 */
export function formatMillisecondsToHoursMinutes(milliseconds) {
  // Проверка на то, что переданное значение является числом
  if (typeof milliseconds !== 'number' || isNaN(milliseconds)) {
    console.error('Переданное значение не является числом');
    return null; // Возвращаем null при ошибке
  }

  // Проверка на то, что число неотрицательное
  if (milliseconds < 0) {
    console.error('Переданное количество миллисекунд не может быть отрицательным');
    return null; // Возвращаем null при ошибке
  }

  // Преобразование миллисекунд в часы и минуты
  const totalMinutes = Math.floor(milliseconds / 60000); // 1 минута = 60000 миллисекунд
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  // Форматирование строку "HH:mm"
  const formattedHours = String(hours).padStart(2, '0'); // Дополнение до двух цифр с помощью '0'
  const formattedMinutes = String(minutes).padStart(2, '0'); // Дополнение до двух цифр с помощью '0'

  return `${formattedHours}:${formattedMinutes}`;
}

/**
 * Преобразует миллисекунды в дату в формате DD.MM.YYYY [HH:mm:ss] в московском часовом поясе
 *
 * @param {number} milliseconds - количество миллисекунд с начала эпохи Unix.
 * @returns {string|null} строковое представление даты в формате DD.MM.YYYY HH:mm:ss или null в случае ошибки.
 */
export function convertMillisecondsToDate(milliseconds, withTime = true) {
  // Проверка, что входной параметр является числом
  if (typeof milliseconds !== 'number' || isNaN(milliseconds)) {
    // console.error('Переданное значение не является числом');
    return null;
  }

  // Создание объекта даты с учетом временной зоны 'Europe/Moscow'
  const date = moment(milliseconds).tz('Europe/Moscow');

  const format = withTime ? 'DD.MM.YYYY HH:mm:ss' : 'DD.MM.YYYY';

  // Форматируем дату в московском часовом поясе
  return date && date.isValid() ? date.format(format) : '-';
}

/**
 * Перевод выбранной даты из календаря (полночь выбранной даты в часовом поясе, из которого открывают веб аппку) в миллисекунды полночи этой же даты по мск
 * @param {Date} value дата из дейтпикера
 * @returns {number} миллисекунды по московскому времени
 */
export function convertDateToMs(value) {
  if (value instanceof Date) {
    const offsetMinsToUtc = value.getTimezoneOffset();
    const offsetMinsMoscowToUtc = -moment().tz('Europe/Moscow').utcOffset(); // минус потому что getTimezoneOffset возвращает отрицательное значение, а utcOffset положительное
    // Получаем смещение временной зоны в минутах и переводим его в миллисекунды
    const offset = (offsetMinsToUtc - offsetMinsMoscowToUtc) * 60 * 1000;
    const moscowMidnightMs = moment(value).valueOf() - offset;
    return moscowMidnightMs;
  }
  return value;
}

/**
 * Преобразует входную дату в объект Date, соответствующий 00:00 по московскому времени.
 *
 * @param {string | Date} inputDate - Исходная дата.
 * @returns {Date | null} - Новый объект Date с временем 00:00 по МСК или null при ошибке.
 */
export function convertToMoscowStartOfDay(inputDate) {
  if (!inputDate || !moment(inputDate).isValid()) {
    return null;
  }

  // Приводим дату к МСК и устанавливаем начало дня (00:00 по Москве)
  const moscowMoment = moment.tz(inputDate, 'Europe/Moscow').startOf('day');

  // Возвращаем как обычный Date — он будет в UTC, соответствующем 00:00 по Москве
  return moscowMoment.toDate();
}

/**
 * Преобразует указанные поля объекта в дату начала дня по Москве.
 * 
 * @param {Object} obj - Исходный объект (например, formData)
 * @param {string[]} keys - Массив ключей, которые нужно преобразовать
 * @returns {Object} Новый объект с преобразованными полями
 */
export function transformFieldsToMoscowStart(obj, keys) {
  if (!obj || typeof obj !== 'object') return obj;

  // Клонируем объект, чтобы не мутировать исходный
  const newObj = { ...obj };

  keys.forEach((key) => {
    if (newObj[key]) {
      newObj[key] = convertToMoscowStartOfDay(newObj[key]);
    }
  });

  return newObj;
}
